import { interceptorMiddleware } from '../../../shared/services/interceptor.service';
import { createApi } from '@reduxjs/toolkit/query/react';
import { ListResponse } from '../../../shared/utils/types.elements';
import { ConstNumber } from '../../../shared/constants/number.constant';
import {
    ApiParamEnum,
    DateFormats,
    HttpMethods,
} from '../../../shared/constants/application.constant';
import { apiUrlAdvertising } from '../constants/apiUrl.advertising';
import {
    Advertisement,
    CreateAdvertisementForm,
} from '../constants/types.advertising';
import { MetaDataResponse } from '../../../shared/types/type';
import { apiUrlBusinessInfo } from '../../businessInfo/utils/apiUrl.businessInfo';
import { getDataRequest, getUpdatedValues } from '../utils/utils';
import moment from 'moment';

// Define a service using a base URL and expected endpoints
export const advertisingApi = createApi({
    reducerPath: 'advertising',
    baseQuery: interceptorMiddleware,
    tagTypes: ['advertising'],
    endpoints: (builder) => ({
        getAdvertisements: builder.query<
            ListResponse<Advertisement>,
            { page: number; adType: number; userId?: number }
        >({
            query: ({ page, adType, userId }) => ({
                url: `${apiUrlAdvertising.ADVERTISEMENT}?limit=${
                    ApiParamEnum.LIMIT
                }&offset=${
                    (page - ConstNumber.VALUE_1) * ApiParamEnum.OFFSET
                }&past_ads=${adType}&user_id=${userId}`,
                method: HttpMethods.GET,
            }),
            providesTags: ['advertising'],
        }),
        getMetaData: builder.query<MetaDataResponse, {}>({
            query: () => ({
                url: apiUrlBusinessInfo.GET_META,
                method: HttpMethods.GET,
            }),
            providesTags: ['advertising'],
        }),
        createAdvertisement: builder.mutation({
            query: (data: CreateAdvertisementForm) => {
                const requestData = {
                    name: data.name,
                    age_target: Number(data.age_target),
                    allow_views: Number(data.views),
                    start_datetime: `${moment(data.start_datetime).format(
                        DateFormats.YYYY_MM_DD
                    )} 00:00`,
                    placement: Number(data.placement),
                    user_payment_plan: data.user_payment_plan,
                    document:
                        typeof data?.document === 'string'
                            ? data?.document
                            : data?.s3MediaUrl,
                };
                return {
                    url: apiUrlAdvertising.ADVERTISEMENT,
                    method: HttpMethods.POST,
                    body: requestData,
                };
            },
        }),
        getAdvertisementById: builder.query<
            Advertisement,
            { id: number; adType: number }
        >({
            query: ({ id, adType }) => ({
                url: `${apiUrlAdvertising.ADVERTISEMENT}${id}/?past_ads=${adType}`,
                method: HttpMethods.GET,
            }),
            providesTags: ['advertising'],
            keepUnusedDataFor: ConstNumber.VALUE_1,
        }),
        updateAdvertisement: builder.mutation<
            {
                message: string;
            },
            {
                data: CreateAdvertisementForm;
                initialValues: CreateAdvertisementForm;
            }
        >({
            query: ({ data, initialValues }) => {
                const dataRequest = getDataRequest(data);
                const changedValues = getUpdatedValues(
                    initialValues,
                    dataRequest
                );
                if ('document' in changedValues) {
                    changedValues['document'] =
                        changedValues['s3MediaUrl'] || '';
                }
                delete changedValues['s3MediaUrl'];
                return {
                    url: `${apiUrlAdvertising.ADVERTISEMENT}${data?.id}/`,
                    method: HttpMethods.PATCH,
                    body: changedValues,
                };
            },
        }),
        getTransactionStatus: builder.query<
            {
                message: string;
            },
            { id: number }
        >({
            query: ({ id }) => ({
                url: `${apiUrlAdvertising.TRANSACTION_UPDATE}${id}/`,
                method: HttpMethods.GET,
            }),
            providesTags: ['advertising'],
            keepUnusedDataFor: ConstNumber.VALUE_1,
        }),
    }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
    useGetAdvertisementsQuery,
    useGetMetaDataQuery,
    useCreateAdvertisementMutation,
    useGetAdvertisementByIdQuery,
    useUpdateAdvertisementMutation,
    useLazyGetTransactionStatusQuery,
} = advertisingApi;
